/**
 * Pipeline and node validation utilities
 */

import type { PipelineDefinition, PipelineNode } from '../types/pipeline';
import { isBindingComplete } from '../components/ValueBindingEditor';
import type { OutputValueBindingConfig } from '../components/ValueBindingEditor';
import { isElementBindingComplete } from '../components/PushbackLoopEditor';
import type { PushbackLoopConfig } from '../components/PushbackLoopEditor';

export interface ValidationError {
  nodeId?: string;
  nodeLabel?: string;
  field?: string;
  message: string;
}

/**
 * Validate entire pipeline before save or execution
 */
export function validatePipeline(pipeline: PipelineDefinition): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check if pipeline has nodes
  if (pipeline.nodes.length === 0) {
    errors.push({
      message: 'Pipeline must have at least one node',
    });
    return errors;
  }

  // Note: node-level config is no longer validated here - data source nodes
  // (item/tracker/project) are type-only declarations with no required fields,
  // and other node types don't currently have save-time-required config either.

  // Branch nodes are the exception: the backend's ConditionalStep.condition is a
  // required field, so an unconfigured branch node produces an opaque 400 on save.
  for (const node of pipeline.nodes) {
    if (node.task_type !== 'branch') continue;
    const condition = node.config?.condition;
    if (!condition?.upstream_step_reference || !condition?.field || !condition?.operator) {
      errors.push({
        nodeId: node.id,
        nodeLabel: node.label || node.id,
        field: 'condition',
        message: 'Branch node needs a condition configured (double-click it to set one) before saving',
      });
    }
  }

  // Output nodes mirror the backend's CreateItemStep/PushbackStep validation, which
  // would otherwise also surface only as a 400 on save.
  const nodeIds = new Set(pipeline.nodes.map((n) => n.id));
  const bindingError = (node: PipelineNode, binding: OutputValueBindingConfig | undefined, what: string) => {
    if (!binding || !isBindingComplete(binding)) {
      return `${what} needs a complete value source (double-click the node to configure it)`;
    }
    if (binding.source === 'step_output' && !nodeIds.has(binding.upstream_step_reference || '')) {
      return `${what} reads from an upstream node that no longer exists`;
    }
    if (binding.source === 'step_output' && binding.upstream_step_reference === node.id) {
      return `${what} can't read its own output`;
    }
    return null;
  };

  for (const node of pipeline.nodes) {
    const nodeLabel = node.label || node.id;
    if (node.task_type === 'pushback' && node.config?.loop) {
      const loop: PushbackLoopConfig = node.config.loop;
      const listMessage = bindingError(node, loop.items_binding, 'Pushback list');
      if (listMessage) errors.push({ nodeId: node.id, nodeLabel, field: 'loop', message: listMessage });
      const elementBindings: [string, PushbackLoopConfig['target_field'] | undefined][] = [
        ['target field', loop.target_field],
        ['value', loop.value],
        ...(loop.operation ? [['operation', loop.operation] as [string, PushbackLoopConfig['target_field']]] : []),
      ];
      for (const [what, binding] of elementBindings) {
        if (!isElementBindingComplete(binding)) {
          errors.push({ nodeId: node.id, nodeLabel, field: 'loop', message: `Pushback list item ${what} is not configured` });
        }
      }
    } else if (node.task_type === 'pushback') {
      if (node.config?.target_field_binding) {
        const targetMessage = bindingError(node, node.config.target_field_binding, 'Pushback target field');
        if (targetMessage) errors.push({ nodeId: node.id, nodeLabel, field: 'target_field', message: targetMessage });
      } else if (!node.config?.target_field?.trim()) {
        errors.push({ nodeId: node.id, nodeLabel, field: 'target_field', message: 'Pushback node needs a target field' });
      }
      const message = bindingError(node, node.config?.value_binding, 'Pushback value');
      if (message) errors.push({ nodeId: node.id, nodeLabel, field: 'value_binding', message });
    }

    if (node.task_type === 'create_item') {
      const config = node.config || {};
      if (!config.target_scope_id?.trim() || !config.target_project_id?.trim() || !config.target_type_id?.trim()) {
        errors.push({
          nodeId: node.id,
          nodeLabel,
          field: 'target',
          message: 'Create Item node needs a target tracker (scope, project and tracker id)',
        });
      }
      const mappings: { target_field?: string; value?: OutputValueBindingConfig }[] = config.field_mappings || [];
      if (mappings.length === 0) {
        errors.push({ nodeId: node.id, nodeLabel, field: 'field_mappings', message: 'Create Item node needs at least one field mapping' });
      }
      for (const mapping of mappings) {
        const fieldName = mapping.target_field?.trim();
        if (!fieldName) {
          errors.push({ nodeId: node.id, nodeLabel, field: 'field_mappings', message: 'A field mapping has no target field' });
          continue;
        }
        const message = bindingError(node, mapping.value, `Field '${fieldName}'`);
        if (message) errors.push({ nodeId: node.id, nodeLabel, field: 'field_mappings', message });
      }
    }
  }

  // Note: We don't validate connectivity - workflows can have independent nodes
  // that run in parallel and just store results without connecting to other nodes

  return errors;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) return '';

  const lines: string[] = ['Pipeline validation failed:'];

  for (const error of errors) {
    if (error.nodeLabel) {
      lines.push(`  • ${error.nodeLabel}: ${error.message}`);
    } else {
      lines.push(`  • ${error.message}`);
    }
  }

  return lines.join('\n');
}
